import { useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { useSessionCompetenciesIDs } from '../../api/SessionResponsesAPI';
import { useCompetenciesByIds } from '../../api/CompetenciesAPI';

const CompetencySelector = ({ onCompetencySelect, selectedCompetencies, sessionId }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('all');

    const { 
        selectedCompetencies: competencyIds, 
        isLoading: idsLoading, 
        error: idsError 
    } = useSessionCompetenciesIDs(sessionId);
    
    const {
        data: competencies,
        isLoading: competenciesLoading,
        error: competenciesError
    } = useCompetenciesByIds(competencyIds || []);

    
    const phases = [
        { id: 1, title: "Empathize" },
        { id: 2, title: "Define" },
        { id: 3, title: "Ideate" },
        { id: 4, title: "Prototype" },
        { id: 5, title: "Test" },
        { id: 6, title: "Communicate" }
    ];

    const columnDefs = [
        { 
            field: 'name',
            headerName: 'Competency',
            flex: 2,
            sortable: true,
            filter: true
        },
        { 
            field: 'abilities.name',
            headerName: 'Ability',
            flex: 1,
            sortable: true,
            filter: true,
            valueGetter: params => params.data?.abilities?.name || ''
        },
        { 
            field: 'abilities.phase',
            headerName: 'Phase',
            flex: 1,
            sortable: true,
            filter: true,
            valueGetter: params => params.data?.abilities?.phase || ''
        },
        {
            headerName: 'Select',
            flex: 1,
            cellRenderer: params => {
                const isSelected = selectedCompetencies.some(c => c.id === params.data.id);
                const atMaxSelections = selectedCompetencies.length >= 8 && !isSelected;
                
                return (
                    <div className="flex items-center justify-center h-full">
                        <label className="inline-flex items-center">
                            <input
                                type="checkbox"
                                checked={isSelected}
                                disabled={atMaxSelections}
                                onChange={() => handleCompetencyToggle(params.data)}
                                className={`form-checkbox h-5 w-5 rounded ${
                                    atMaxSelections 
                                        ? 'text-gray-400 cursor-not-allowed' 
                                        : 'text-blue-500 cursor-pointer'
                                }`}
                            />
                        </label>
                    </div>
                );
            }
        }
    ];

    const handleCompetencyToggle = (competency) => {
        const isAlreadySelected = selectedCompetencies.some(c => c.id === competency.id);
        
        if (isAlreadySelected) {
            onCompetencySelect(selectedCompetencies.filter(c => c.id !== competency.id));
        } else if (selectedCompetencies.length < 8) {
            const formattedCompetency = {
                id: competency.id,
                name: competency.name || ''
            };
            
            onCompetencySelect([...selectedCompetencies, formattedCompetency]);
        }
    };

    const getFilteredCompetencies = () => {
        if (!competencies) return [];
        
        if (activeTab === 'all') return competencies;
        
        if (activeTab === 'selected') {
            // For the "Selected" tab, filter to show only selected competencies
            return competencies.filter(comp => 
                selectedCompetencies.some(selected => selected.id === comp.id)
            );
        }
        
        // For phase tabs
        return competencies.filter(comp => 
            comp.abilities?.phase === activeTab
        );
    };

    const handleModalOpen = () => {
        setIsModalOpen(true);
    };

    // Show loading state
    if (idsLoading || competenciesLoading) {
        return (
            <button className="w-full py-3 px-4 text-left border rounded-md bg-white">
                <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900 mr-2"></div>
                    <span>Loading competencies...</span>
                </div>
            </button>
        );
    }

    // Show error state
    if (idsError || competenciesError) {
        return (
            <button className="w-full py-3 px-4 text-left border rounded-md bg-white text-red-500">
                Error loading competencies
            </button>
        );
    }

    return (
        <>
            <button
                onClick={handleModalOpen}
                className="w-full py-3 px-4 text-left border rounded-md hover:border-gray-300 flex items-center gap-2 bg-white"
            >
                <div className="flex-1">
                    <div className="flex justify-between items-center mb-1">
                        <span className="font-medium text-gray-800">
                            Select Competencies (up to 8)
                        </span>
                        <span className="text-sm text-gray-500">
                            {selectedCompetencies.length}/8
                        </span>
                    </div>
                    <p className="text-sm text-gray-500">
                        Click to add competencies from different phases
                    </p>
                </div>
                <span className="text-gray-400 text-xl">+</span>
            </button>

            {isModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto">
                    <div className="min-h-full flex items-start justify-center p-4 sm:p-6">
                    <div className="bg-white rounded-lg w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
                        {/* Modal Header */}
                        <div className="px-6 py-4 border-b flex justify-between items-center">
                            <h2 className="text-xl font-semibold">Select Competencies ({selectedCompetencies.length}/8)</h2>
                            <button 
                                onClick={() => setIsModalOpen(false)}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                ×
                            </button>
                        </div>

                        {/* Tabs */}
                        <div className="border-b overflow-x-auto">
                            <div className="flex px-6">
                                {/* Selected tab - new addition */}
                                <button
                                    onClick={() => setActiveTab('selected')}
                                    className={`py-3 px-4 border-b-2 -mb-px whitespace-nowrap ${
                                        activeTab === 'selected'
                                            ? 'border-blue-500 text-blue-600'
                                            : 'border-transparent text-gray-500 hover:text-gray-700'
                                    }`}
                                >
                                    Selected ({selectedCompetencies.length})
                                </button>
                                
                                <button
                                    onClick={() => setActiveTab('all')}
                                    className={`py-3 px-4 border-b-2 -mb-px whitespace-nowrap ${
                                        activeTab === 'all'
                                            ? 'border-blue-500 text-blue-600'
                                            : 'border-transparent text-gray-500 hover:text-gray-700'
                                    }`}
                                >
                                    All Competencies
                                </button>
                                
                                {phases.map(phase => (
                                    <button
                                        key={phase.id}
                                        onClick={() => setActiveTab(phase.title)}
                                        className={`py-3 px-4 border-b-2 -mb-px whitespace-nowrap ${
                                            activeTab === phase.title
                                                ? 'border-blue-500 text-blue-600'
                                                : 'border-transparent text-gray-500 hover:text-gray-700'
                                        }`}
                                    >
                                        {phase.title}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* AG Grid Table */}
                        <div className="p-6 flex-1 min-h-0 overflow-y-auto">
                            {activeTab === 'selected' && selectedCompetencies.length === 0 ? (
                                <div className="flex flex-col items-center justify-center h-[450px] text-gray-500">
                                    <svg className="w-16 h-16 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <p className="text-center">No competencies selected yet.</p>
                                    <p className="text-center text-sm mt-2">Select competencies from other tabs to see them here.</p>
                                </div>
                            ) : (
                                <div className="ag-theme-alpine w-full h-[450px]">
                                    <AgGridReact
                                        columnDefs={columnDefs}
                                        rowData={getFilteredCompetencies()}
                                        pagination={true}
                                        paginationPageSize={10}
                                        suppressCellFocus={true}
                                    />
                                </div>
                            )}
                        </div>

                        {/* Modal Footer */}
                        <div className="px-6 py-4 border-t flex justify-end">
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="px-6 py-2 bg-primary text-white rounded-md hover:bg-blue-700"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default CompetencySelector; 